import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center" data-testid="pp-logo-link">
              <img src="https://customer-assets.emergentagent.com/job_38ea2e2e-0b2a-4c96-90e7-893185248e11/artifacts/fcaqs56k_logo.png" alt="Guardifix" className="h-8" />
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2" data-testid="pp-back-btn">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-28 pb-16">
        <div className="space-y-8">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Privacy Policy</h1>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                At Guardifix, privacy is not just a feature — it is the foundation of everything we build. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our encrypted email platform. Our zero-knowledge architecture ensures that only you can access your communications.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Information We Collect</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">Due to our zero-knowledge encryption, we cannot access the content of your emails or attachments. We may collect:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Account information (email address, display name)</li>
                <li>Payment details (processed through PCI-compliant third-party providers)</li>
                <li>Technical metadata (IP address, browser type, device identifiers)</li>
                <li>Aggregated usage statistics (login frequency, feature adoption)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Your Information</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">Collected information is used to:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Deliver and maintain the Guardifix email platform</li>
                <li>Process transactions and deliver service notifications</li>
                <li>Improve performance and develop new capabilities</li>
                <li>Detect, investigate, and prevent fraudulent or abusive activity</li>
                <li>Meet applicable legal and regulatory obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Data Security</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Guardifix employs AES-256 encryption at rest and RSA-4096 for key exchange. All messages are encrypted on your device before reaching our servers. Even under legal compulsion, we cannot decrypt your data. Our infrastructure operates in jurisdictions with strong data-protection statutes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Sharing and Disclosure</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">We do not sell, rent, or trade your personal data. Limited disclosure occurs only when:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>You grant explicit written consent</li>
                <li>Required by enforceable legal process</li>
                <li>Necessary to protect our rights, safety, or property</li>
                <li>Shared with vetted service providers under strict confidentiality agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Your Rights</h2>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Access your stored personal information</li>
                <li>Request correction of inaccurate records</li>
                <li>Request complete deletion of your account and data</li>
                <li>Object to specific processing activities</li>
                <li>Export your data in a machine-readable format</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Cookies and Tracking</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                We use only essential cookies required for authentication and session management. No advertising or behavioral-tracking cookies are deployed. You may manage cookie settings through your browser.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. International Data Transfers</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Guardifix servers are located in privacy-focused jurisdictions with robust data-protection frameworks. If you access the platform from outside these regions, your metadata may be transferred to and processed within these countries.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Guardifix is not intended for users under 16. We do not knowingly collect personal information from minors. If we discover such data has been collected, we will promptly delete it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Policy Updates</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                We may revise this Privacy Policy periodically. Material changes will be communicated by posting the updated version on this page. Continued use of the platform after revisions constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Questions about this Privacy Policy or our data practices? Reach us at:
              </p>
              <p className="text-slate-600 text-sm mt-3">
                <strong>Email:</strong> info@guardifix.com
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
